import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as KpiReportSearchFormInitialData,
  ValidData as KpiReportSearchFormValidData,
} from '@/components/searchForms/KpiReportSearchForm.vue';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import { fetchGetKpiReport } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { ReportResponse } from '@/models/reports';

export interface KpiReportViewComposable {
  search: (formData: KpiReportSearchFormValidData) => void;
  initialData: ComputedRef<KpiReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  total: Ref<number>;
  kpiReportResults: Ref<ReportResponse[] | null>;
  resultsForCsv: Ref<ResultItem[]>;
}

export default function useKpiReportView(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
  queryCustomer: Readonly<Ref<string | undefined>>,
): KpiReportViewComposable {
  const router = useRouter();
  const route = useRoute();

  const initialData = computed<KpiReportSearchFormInitialData>(() => {
    return {
      customer: queryCustomer.value,
    };
  });

  const isFetchingData = ref(false);
  const kpiReportResults = ref<ReportResponse[] | null>(null);

  const total = computed(() => {
    if (!Array.isArray(kpiReportResults.value)) return 0;
    return kpiReportResults.value.length;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    if (!Array.isArray(kpiReportResults.value)) return [];
    return kpiReportResults.value.map((item) => {
      const { content, ...resultItem } = item;
      return resultItem;
    });
  });

  function search(formData: KpiReportSearchFormValidData): void {
    const query = { ...route.query, ...formData };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.KPI_REPORT,
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
    // TODO page/page_size for endpoint
    const curPage = queryCurPage.value;
    const pageSize = queryPageSize.value;
    /* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */
    const customer = queryCustomer.value;
    kpiReportResults.value = null;
    if (!customer) return;
    isFetchingData.value = true;
    await fetchGetKpiReport(customer)
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        kpiReportResults.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.KPI_REPORT) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    total,
    kpiReportResults,
    resultsForCsv,
  };
}
